import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Icon from '../Icon';
import ActionButton from '../ActionButtons/ActionButton';
import { utils } from '@ohif/core';
import DeleteMeasurementModal from '../ConfirmModal/DeleteMeasurementModal';

const MeasurementItem = ({
  item,
  uid,
  index,
  label,
  displayText,
  isActive,
  isSaved = false,
  onClick,
  onEdit,
  onDelete,
}) => {
  const [isHovering, setIsHovering] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const iconName = item.toolName === 'SplineROI' ? 'icon-tool-spline-roi' : 'tool-length';

  const onEditHandler = event => {
    event.stopPropagation();
    onEdit({ uid, isActive, event });
  };

  const openDeleteModal = event => {
    event.stopPropagation();
    setIsModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsModalOpen(false);
  };

  const confirmDelete = uidToDelete => {
    // deleteFromVIDAADB(uidToDelete);
    onDelete?.(uidToDelete);
    setIsModalOpen(false);
  };

  const onClickHandler = event => onClick({ uid, isActive, event });

  const onMouseEnter = () => setIsHovering(true);
  const onMouseLeave = () => setIsHovering(false);

  return (
    <>
      <div className="ml-2 mb-3 flex items-center space-x-1">
        <div
          role="button"
          tabIndex={0}
          className={classnames(
            'group flex w-full min-w-0 cursor-pointer overflow-visible rounded outline-none transition-all duration-300 ease-in-out',
            {
              'border-2 border-black bg-[#d9d9d9]': isActive,
              'border border-black bg-white': !isActive,
            }
          )}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          onClick={onClickHandler}
          onKeyDown={event => {
            if (event.key === 'Enter' || event.key === ' ') {
              onClickHandler(event as any);
              event.preventDefault();
            }
          }}
          data-cy={'measurement-item'}
        >
          <div className="relative flex flex-row items-center gap-2 py-1 pl-2 pr-1">
            <Icon
              name={iconName}
              className="w-5 text-black transition-colors duration-200 hover:text-gray-700"
            />
            <div className="flex flex-col">
              <div className="flex items-center gap-1">
                <span className="text-base font-bold text-black">{label}</span>
                {!isSaved && (
                  <span
                    className="text-lg font-bold text-red-600"
                    title="Not saved to database"
                  >
                    *
                  </span>
                )}
              </div>
              <div className="flex flex-col leading-tight">
                {displayText.map((line, i) => (
                  <span
                    key={i}
                    className="text-sm text-black"
                    dangerouslySetInnerHTML={{ __html: line }}
                  ></span>
                ))}
              </div>
            </div>
          </div>

          <div className="ml-auto flex space-x-2 py-1 pr-2">
            <ActionButton
              className="flex cursor-pointer bg-transparent p-1"
              tooltip="Change measurement name"
              onClick={onEditHandler}
            >
              <Icon
                name="pencil"
                className="w-5 text-black transition-colors duration-200 hover:text-gray-700"
              />
            </ActionButton>

            <ActionButton
              className="flex cursor-pointer bg-transparent p-1"
              tooltip={isActive ? 'Hide measurement' : 'View measurement'}
              onClick={onClickHandler}
            >
              <Icon
                name="filled-circle"
                className={isActive ? 'w-5 text-green-600' : 'w-5 text-yellow-400'}
              />
            </ActionButton>
          </div>
        </div>

        <ActionButton
          className="flex cursor-pointer items-center justify-center bg-transparent p-2"
          tooltip="Remove measurement"
          onClick={openDeleteModal}
        >
          <Icon
            name="old-trash"
            className="w-4 text-black transition-colors duration-200 hover:text-gray-700"
          />
        </ActionButton>
      </div>

      <DeleteMeasurementModal
        isOpen={isModalOpen}
        measurement={{ uid, label, displayText }}
        onClose={closeDeleteModal}
        onConfirm={confirmDelete}
      />
    </>
  );
};

MeasurementItem.propTypes = {
  uid: PropTypes.oneOfType([PropTypes.number.isRequired, PropTypes.string.isRequired]),
  index: PropTypes.number.isRequired,
  label: PropTypes.string,
  displayText: PropTypes.array.isRequired,
  isActive: PropTypes.bool,
  isSaved: PropTypes.bool,
  onClick: PropTypes.func,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
};

MeasurementItem.defaultProps = {
  isActive: false,
};

export default MeasurementItem;
